/**
 * Balance Sheet Service
 *
 * Fetches balance sheet data from the backend API and transforms it
 * for display in the Report Editor bottom panel.
 */

import { get } from './api';
import {
  BalanceSheetData,
  BalanceSheetRow,
  PeriodConfig,
  BigQueryBalanceSheetRow,
} from '../types/balanceSheet';
import {
  ACTIVOS_ACCOUNTS,
  PASIVOS_ACCOUNTS,
  PATRIMONIO_ACCOUNTS,
  RESULTADOS_ACCOUNTS,
  formatPeriodShort,
} from '../config/balanceSheetAccounts';

/**
 * Calculate which periods to display based on the report period
 */
export function calculateDisplayPeriods(
  reportPeriod: string,
  yearsBack: number = 5
): PeriodConfig {
  const [year, month] = reportPeriod.split('-').map(Number);
  const previousYear = year - 1;
  const isDecember = month === 12;

  // 1. Historical December periods (going back yearsBack years)
  const historicalDecPeriods: string[] = [];
  for (let i = yearsBack; i >= 2; i--) {
    historicalDecPeriods.push(`${year - i}-12`);
  }

  // 2. Previous year quarterly periods
  const previousYearQuarters: string[] = [];
  if (isDecember) {
    // For December reports: Mar, Jun, Sep of current year
    previousYearQuarters.push(`${year}-03`, `${year}-06`, `${year}-09`);
  } else {
    // For non-December: all 4 quarters of previous year
    previousYearQuarters.push(
      `${previousYear}-03`,
      `${previousYear}-06`,
      `${previousYear}-09`,
      `${previousYear}-12`
    );
  }

  // 3. All display periods (in chronological order)
  const allDisplayPeriods = [...historicalDecPeriods, ...previousYearQuarters, reportPeriod];

  // 4. Generate YoY pairs from consecutive December periods
  const yoyPairs: Array<{ prev: string; curr: string; label: string }> = [];
  const decemberPeriods = allDisplayPeriods.filter((p) => p.endsWith('-12'));
  for (let i = 0; i < decemberPeriods.length - 1; i++) {
    const prev = decemberPeriods[i];
    const curr = decemberPeriods[i + 1];
    const prevYearShort = prev.split('-')[0].slice(2);
    const currYearShort = curr.split('-')[0].slice(2);
    yoyPairs.push({
      prev,
      curr,
      label: `${prevYearShort}-${currYearShort}`,
    });
  }

  // 5. Add interannual period (same month last year) if not already in list
  const interannualPeriod = `${year - 1}-${month.toString().padStart(2, '0')}`;
  const allPeriodsToFetch = [...new Set([...allDisplayPeriods, interannualPeriod])];

  return {
    reportPeriod,
    historicalDecPeriods,
    previousYearQuarters,
    currentPeriod: reportPeriod,
    allDisplayPeriods,
    yoyPairs,
    allPeriodsToFetch,
  };
}

/**
 * Fetch balance sheet data from the backend
 */
export async function fetchBalanceSheetData(
  institutionId: string,
  reportPeriod: string
): Promise<BalanceSheetData> {
  const periodConfig = calculateDisplayPeriods(reportPeriod);

  // Call backend API
  const response = await get<{
    success: boolean;
    data: BigQueryBalanceSheetRow[];
    institution_name: string;
  }>(`/api/balance-sheet/${institutionId}?periods=${periodConfig.allPeriodsToFetch.join(',')}`);

  if (!response.success) {
    throw new Error('Failed to fetch balance sheet data');
  }

  // Transform raw data into structured format
  return transformBalanceSheetData(
    response.data,
    institutionId,
    response.institution_name,
    periodConfig
  );
}

/**
 * Transform raw BigQuery data into structured balance sheet format
 */
function transformBalanceSheetData(
  rawData: BigQueryBalanceSheetRow[],
  institutionId: string,
  institutionName: string,
  periodConfig: PeriodConfig
): BalanceSheetData {
  // Create a map of account_code -> period -> value
  const valuesByAccount: Record<string, Record<string, number>> = {};

  for (const row of rawData) {
    if (!valuesByAccount[row.balance_sheet_item_code]) {
      valuesByAccount[row.balance_sheet_item_code] = {};
    }
    valuesByAccount[row.balance_sheet_item_code][row.period] = row.balance_sheet_item_value;
  }

  // Build each section
  const sections = {
    activos: buildSection(ACTIVOS_ACCOUNTS, valuesByAccount, periodConfig),
    pasivos: buildSection(PASIVOS_ACCOUNTS, valuesByAccount, periodConfig),
    patrimonio: buildSection(PATRIMONIO_ACCOUNTS, valuesByAccount, periodConfig),
    resultados: buildResultadosSection(valuesByAccount, periodConfig),
  };

  // Calculate participation percentages
  calculateParticipation(sections.activos, '1', periodConfig.currentPeriod);
  calculateParticipation(sections.pasivos, '2', periodConfig.currentPeriod);
  calculateParticipation(sections.patrimonio, '3', periodConfig.currentPeriod);

  // Generate YoY labels for column headers
  const yoyLabels = periodConfig.yoyPairs.map((pair) => pair.label);

  return {
    institution_id: institutionId,
    institution_name: institutionName,
    periods: periodConfig.allDisplayPeriods,
    yoy_labels: yoyLabels,
    sections,
  };
}

/**
 * Build a section from account config and values
 */
function buildSection(
  accounts: typeof ACTIVOS_ACCOUNTS,
  valuesByAccount: Record<string, Record<string, number>>,
  periodConfig: PeriodConfig
): BalanceSheetRow[] {
  return accounts.map((account) => {
    const values: Record<string, number> = {};

    for (const period of periodConfig.allDisplayPeriods) {
      values[period] = valuesByAccount[account.code]?.[period] || 0;
    }

    // Calculate YoY changes
    const yoy_changes: Record<string, number | null> = {};
    for (const pair of periodConfig.yoyPairs) {
      const prevValue = valuesByAccount[account.code]?.[pair.prev];
      const currValue = valuesByAccount[account.code]?.[pair.curr];
      if (prevValue && prevValue !== 0) {
        yoy_changes[pair.label] = ((currValue - prevValue) / Math.abs(prevValue)) * 100;
      } else {
        yoy_changes[pair.label] = null;
      }
    }

    // Calculate interannual (current vs same period last year)
    const [year, month] = periodConfig.reportPeriod.split('-').map(Number);
    const interannualPeriod = `${year - 1}-${month.toString().padStart(2, '0')}`;
    const currentValue = values[periodConfig.reportPeriod];
    const previousYearValue = valuesByAccount[account.code]?.[interannualPeriod];
    let interannual: number | null = null;
    if (previousYearValue && previousYearValue !== 0) {
      interannual = ((currentValue - previousYearValue) / Math.abs(previousYearValue)) * 100;
    }

    return {
      account_code: account.code,
      account_name: account.name,
      indent_level: account.indent,
      is_calculated: false,
      values,
      yoy_changes,
      interannual,
    };
  });
}

/**
 * Build the RESULTADOS section with calculated fields
 */
function buildResultadosSection(
  valuesByAccount: Record<string, Record<string, number>>,
  periodConfig: PeriodConfig
): BalanceSheetRow[] {
  const rows: BalanceSheetRow[] = [];

  // Helper to get value for a period
  const getValue = (code: string, period: string): number => {
    return valuesByAccount[code]?.[period] || 0;
  };

  // Helper to calculate for all periods
  const calculateForAllPeriods = (
    calcFn: (period: string) => number
  ): Record<string, number> => {
    const values: Record<string, number> = {};
    for (const period of [...periodConfig.allDisplayPeriods, ...periodConfig.allPeriodsToFetch]) {
      values[period] = calcFn(period);
    }
    return values;
  };

  // Store calculated values for chained calculations
  const calculatedValues: Record<string, Record<string, number>> = {};

  for (const account of RESULTADOS_ACCOUNTS) {
    let values: Record<string, number>;

    if (account.isCalculated) {
      // Calculate based on formula
      switch (account.code) {
        case 'margen_neto_intereses':
          values = calculateForAllPeriods(
            (p) => getValue('51', p) - getValue('41', p)
          );
          break;
        case 'margen_bruto_financiero':
          values = calculateForAllPeriods(
            (p) =>
              (calculatedValues['margen_neto_intereses']?.[p] || 0) +
              getValue('52', p) +
              getValue('54', p) -
              getValue('42', p) +
              getValue('53', p) -
              getValue('43', p)
          );
          break;
        case 'margen_neto_financiero':
          values = calculateForAllPeriods(
            (p) =>
              (calculatedValues['margen_bruto_financiero']?.[p] || 0) -
              getValue('44', p)
          );
          break;
        case 'margen_intermediacion':
          values = calculateForAllPeriods(
            (p) =>
              (calculatedValues['margen_neto_financiero']?.[p] || 0) -
              getValue('45', p)
          );
          break;
        case 'margen_operacional':
          values = calculateForAllPeriods(
            (p) =>
              (calculatedValues['margen_intermediacion']?.[p] || 0) +
              getValue('55', p) -
              getValue('46', p)
          );
          break;
        case 'ganancias_antes_impuestos':
          values = calculateForAllPeriods(
            (p) =>
              (calculatedValues['margen_operacional']?.[p] || 0) +
              getValue('56', p) -
              getValue('47', p)
          );
          break;
        case '3603':
          values = calculateForAllPeriods(
            (p) =>
              (calculatedValues['ganancias_antes_impuestos']?.[p] || 0) -
              getValue('48', p)
          );
          break;
        default:
          values = {};
      }
      calculatedValues[account.code] = values;
    } else {
      // Raw value from BigQuery
      values = {};
      for (const period of periodConfig.allDisplayPeriods) {
        values[period] = getValue(account.code, period);
      }
    }

    // Calculate YoY changes
    const yoy_changes: Record<string, number | null> = {};
    for (const pair of periodConfig.yoyPairs) {
      const prevValue = values[pair.prev];
      const currValue = values[pair.curr];
      if (prevValue && prevValue !== 0) {
        yoy_changes[pair.label] = ((currValue - prevValue) / Math.abs(prevValue)) * 100;
      } else {
        yoy_changes[pair.label] = null;
      }
    }

    // Calculate interannual
    const [year, month] = periodConfig.reportPeriod.split('-').map(Number);
    const interannualPeriod = `${year - 1}-${month.toString().padStart(2, '0')}`;
    const currentValue = values[periodConfig.reportPeriod];
    const previousYearValue = values[interannualPeriod];
    let interannual: number | null = null;
    if (previousYearValue && previousYearValue !== 0) {
      interannual = ((currentValue - previousYearValue) / Math.abs(previousYearValue)) * 100;
    }

    rows.push({
      account_code: account.code,
      account_name: account.name,
      indent_level: account.indent,
      is_calculated: account.isCalculated || false,
      values,
      yoy_changes,
      interannual,
    });
  }

  return rows;
}

/**
 * Calculate participation percentages for a section
 */
function calculateParticipation(
  rows: BalanceSheetRow[],
  totalCode: string,
  currentPeriod: string
): void {
  const totalRow = rows.find((r) => r.account_code === totalCode);
  const totalValue = totalRow?.values[currentPeriod] || 0;

  for (const row of rows) {
    if (totalValue !== 0) {
      row.participation = (row.values[currentPeriod] / totalValue) * 100;
    } else {
      row.participation = 0;
    }
  }
}

/**
 * Format a number value for display
 * Shows dash for: undefined, null, NaN, 0
 */
export function formatValue(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value) || value === 0) return '-';
  // Format with thousands separator, no decimals
  return new Intl.NumberFormat('es-EC').format(Math.round(value));
}

/**
 * Format a ratio/percentage for display
 * Shows dash for: undefined, null, NaN
 */
export function formatRatio(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  if (Math.abs(value) > 999) return value > 0 ? '>999%' : '<-999%';
  return `${value.toFixed(2)}%`;
}

/**
 * Get CSS class for ratio coloring
 */
export function getRatioClass(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return 'ratio-neutral';
  if (value > 0) return 'ratio-positive';
  if (value < 0) return 'ratio-negative';
  return 'ratio-neutral';
}

/**
 * Format period for column header
 */
export function formatPeriodHeader(period: string): string {
  return formatPeriodShort(period);
}
